/**
 * First-party metrics beacon (client side).
 *
 * Sends access and action events to our own /health/* endpoints, which
 * capture them server-side (app/utils/metrics.ts). No third-party script is
 * loaded and the browser never contacts any third-party host. Full design:
 * docs/plans/2026-07-10-posthog-first-party-beacon-analytics.md.
 *
 *   GET  /health/{prefix}/{uuid}.gif    — page_viewed. The gif loading IS the
 *         event: a single <img> rendered client-side by MetricsPixel
 *         (mounted once in pages/_app.tsx), never per-feature JS calls.
 *   POST /health/{prefix}/{uuid}/event  — explicit action events, fired from
 *         submit success handlers
 *
 * Params:
 *   sid — session id: crypto.randomUUID(), lazily generated on first use and
 *         held in module memory only — no cookies, no storage (partitioned or
 *         blocked inside third-party iframes anyway). Scope is one
 *         pageload/iframe document; it links page_viewed → form_submitted.
 *   o   — organization uuid, passed by the caller from the fetched detail
 *         payload (omitted when unknown; org-scoped routes derive it
 *         server-side from the route uuid)
 *   h   — embedding page URL: the ?hosturl= query param the customer embed
 *         snippet passes into the iframe, else document.referrer
 *   v   — record version ('version1'…'version4'), only for landing pages
 *         where the route prefix doesn't determine it
 */

export interface BeaconContext {
  organization?: string;
  version?: string;
}

let sid: string | undefined;

const getSessionId = (): string => {
  if (!sid) {
    sid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ?
      crypto.randomUUID() :
      `${Date.now().toString(36)}-${Math.random().toString(36)
        .slice(2)}`;
  }

  return sid;
};

/**
 * /health/{prefix}/{uuid} for the current page. Landing pages live at
 * /{uuid} with no prefix segment, so they map to the 'act' prefix; every
 * other tracked route's first segment doubles as the beacon prefix.
 */
const basePath = (): string | null => {
  // window.location may be stubbed out in tests
  const pathname = window.location?.pathname;

  if (typeof pathname !== 'string') {
    return null;
  }

  const segments = pathname.split('/').filter(Boolean);

  if (!segments.length) {
    return null;
  }

  const uuid = segments[segments.length - 1];
  const prefix = segments.length === 1 ? 'act' : segments[0];

  return `/health/${prefix}/${uuid}`;
};

const buildParams = ({
  organization,
  version,
}: BeaconContext): URLSearchParams => {
  const params = new URLSearchParams({ sid: getSessionId() });
  const hostUrl = new URLSearchParams(window.location?.search).get('hosturl') ||
    document.referrer;

  if (organization) {
    params.set('o', organization);
  }

  if (hostUrl) {
    params.set('h', hostUrl);
  }

  if (version) {
    params.set('v', version);
  }

  return params;
};

/**
 * The page_viewed pixel URL for the current page, or null outside the
 * browser and on untracked paths. Rendered as an <img> by the MetricsPixel
 * component — loading the gif is the page-view event.
 */
export const buildPixelSrc = (context: BeaconContext = {}): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const base = basePath();

    return base && `${base}.gif?${buildParams(context)}`;
  } catch {
    // A metrics failure must never break the page.
    return null;
  }
};

/**
 * Registers a successful submission (form signup, action center message, or
 * officials lookup). sendBeacon survives post-submit navigation; falls back
 * to keepalive fetch.
 */
export const trackFormSubmit = (context: BeaconContext = {}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const base = basePath();

    if (!base) {
      return;
    }

    const body = buildParams(context);

    body.set('event', 'form_submitted');

    const url = `${base}/event`;

    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url, body)) {
      return;
    }

    fetch(url, {
      body,
      keepalive: true,
      method: 'POST',
    }).catch(() => undefined);
  } catch {
    // A metrics failure must never break the page.
  }
};

/**
 * The beacon session id, for inclusion in submission payloads (sessionId,
 * session_id after the snake_case interceptor) so backend records can be
 * joined to analytics events.
 */
export const getAnalyticsSessionId = (): string | undefined => (
  typeof window === 'undefined' ? undefined : getSessionId()
);
